import React, { useCallback, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { GestureDetector } from 'react-native-gesture-handler';
import Animated from 'react-native-reanimated';
import Svg, { Circle, Path } from 'react-native-svg';

import { Box } from '@/components/lib/components';
import { Row } from '@/components/lib/components/Row';
import { ds } from '@/components/lib/design.system';
import { common, designSystem } from '@/styles/styles';

import { useRadialSlider } from './hooks/use-radial-slider';
import { useSliderAnimation } from './hooks/use-slider-animation';

const MIN = 0; // Minimum value of slider
const RADIUS = 124; // Radius of slider
const OPENING_RADIAN = Math.PI / 4.2; // Opening angle of slider

// Slider thumb
const THUMB_RADIUS = 18;
const THUMB_COLOR = ds.colorValue.background.neutral.inverse;

// Slider track
const SLIDER_WIDTH = 42;
const SLIDER_COLOR = ds.colorValue.background.brand.emphasis;
const SLIDER_BORDER_COLOR = designSystem.colors.neutral700;
const SLIDER_TRACK_COLOR = designSystem.colors.neutral800;
const SLIDER_PADDING = 2;
const SLIDER_BORDER_WIDTH = 1;

// Marker buttons
const MARKER_RADIUS = 3;
const MARKER_TOUCH_RADIUS = 24;
const MARKER_COLOR = designSystem.colors.neutral400;

const AnimatedPath = Animated.createAnimatedComponent(Path);

type RadialSliderProps = {
    centerContent: React.ReactNode;
    topContent: React.ReactNode;
    onValueChange: (value: number) => void;
    max: number;
    initialValue: number;
    minAllowable?: number;
};

export const RadialSlider = ({
    centerContent,
    topContent,
    onValueChange,
    max,
    initialValue,
    minAllowable = MIN,
}: RadialSliderProps) => {
    const containerRef = useRef<View>(null);

    const { svgSize, startPoint, endPoint, startRadian, radianValue, markers } = useRadialSlider({
        radius: RADIUS,
        sliderWidth: SLIDER_WIDTH,
        openingRadian: OPENING_RADIAN,
        thumbRadius: THUMB_RADIUS,
        max: max,
        sliderPadding: SLIDER_PADDING,
        sliderBorderWidth: SLIDER_BORDER_WIDTH,
    });

    const { handlePanGesture, animatedSliderProps, updateThumbPosition, animatedThumbStyle } = useSliderAnimation({
        radius: RADIUS,
        sliderWidth: SLIDER_WIDTH,
        openingRadian: OPENING_RADIAN,
        thumbRadius: THUMB_RADIUS,
        min: MIN,
        max: max,
        onChange: onValueChange,
        value: initialValue,
        minAllowable,
        sliderPadding: SLIDER_PADDING,
        sliderBorderWidth: SLIDER_BORDER_WIDTH,
    });

    const handleMarkerPress = useCallback(
        (value: number) => {
            updateThumbPosition(value);
            onValueChange(value);
        },
        [updateThumbPosition, onValueChange]
    );

    return (
        <Box ref={containerRef} justifyContent="center" alignItems="center" width={svgSize} height={svgSize}>
            <Svg width={svgSize} height={svgSize}>
                {/* Border Path */}
                <Path
                    strokeWidth={SLIDER_WIDTH + SLIDER_PADDING * 2 + SLIDER_BORDER_WIDTH * 2}
                    stroke={SLIDER_BORDER_COLOR}
                    fill="none"
                    strokeLinecap="round"
                    d={`M${startPoint.x},${startPoint.y} A ${RADIUS},${RADIUS},0,${
                        startRadian - radianValue >= Math.PI ? '1' : '0'
                    },1,${endPoint.x},${endPoint.y}`}
                />
                <Path
                    strokeWidth={SLIDER_WIDTH + SLIDER_PADDING * 2}
                    stroke={SLIDER_TRACK_COLOR}
                    fill="none"
                    strokeLinecap="round"
                    d={`M${startPoint.x},${startPoint.y} A ${RADIUS},${RADIUS},0,${
                        startRadian - radianValue >= Math.PI ? '1' : '0'
                    },1,${endPoint.x},${endPoint.y}`}
                />
                {markers.map(({ point, value }) => {
                    return (
                        <React.Fragment key={value}>
                            {/* Visible marker */}
                            <Circle cx={point.x} cy={point.y} r={MARKER_RADIUS} fill={MARKER_COLOR} />
                            {/* Invisible larger touch target */}
                            <Circle
                                cx={point.x}
                                cy={point.y}
                                r={MARKER_TOUCH_RADIUS}
                                fill="transparent"
                                onPress={() => handleMarkerPress(value)}
                                pointerEvents="auto"
                            />
                        </React.Fragment>
                    );
                })}
                <AnimatedPath
                    strokeWidth={SLIDER_WIDTH}
                    stroke={SLIDER_COLOR}
                    fill="none"
                    strokeLinecap="round"
                    animatedProps={animatedSliderProps}
                />
            </Svg>
            <Box height={svgSize} width={svgSize} position="absolute" pointerEvents="box-none">
                <GestureDetector gesture={handlePanGesture}>
                    <Animated.View style={[styles.thumb, animatedThumbStyle]} />
                </GestureDetector>
            </Box>
            <Row
                position="absolute"
                justifyContent="center"
                left={0}
                top={24}
                bottom={0}
                right={0}
                style={common.zIndexNegative1}
            >
                {topContent}
            </Row>
            <Row
                position="absolute"
                justifyContent="center"
                alignItems="center"
                left={0}
                top={0}
                bottom={0}
                right={0}
                style={common.zIndexNegative1}
            >
                {centerContent}
            </Row>
        </Box>
    );
};

const styles = StyleSheet.create({
    thumb: {
        borderRadius: THUMB_RADIUS,
        backgroundColor: THUMB_COLOR,
        width: THUMB_RADIUS * 2,
        height: THUMB_RADIUS * 2,
        position: 'absolute',
        zIndex: 1,
        shadowColor: '#000000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 4, // For Android
    },
});
